import React, { useEffect, useMemo } from "react";
import { Card, CardBody, Col, Container, Row, Spinner } from "reactstrap";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";

import AdatisLogo from "../../Components/Common/AdatisLogo";
import ParticlesAuth from "../AuthenticationInner/ParticlesAuth";

function isLikelyMobileDevice(): boolean {
  if (typeof navigator === "undefined") {
    return false;
  }

  return /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent);
}

/**
 * Build a short deep link — Android intent URLs truncate long query strings
 * (Google adds scope/authuser/prompt). Only forward code/error for token exchange.
 */
function buildAppOAuthDeepLink(search: string): string {
  const incoming = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  const outgoing = new URLSearchParams();

  const code = incoming.get("code");
  const error = incoming.get("error");

  if (code) {
    outgoing.set("code", code);
  }

  if (error) {
    outgoing.set("error", error);
  }

  const query = outgoing.toString();

  return query === "" ? "adatis://auth/google" : `adatis://auth/google?${query}`;
}

/**
 * HTTPS bridge for Expo Google OAuth (GAP-F1-247).
 * Google only accepts HTTPS on Web OAuth clients. The page immediately deep-links
 * to adatis:// so AuthSession / Linking can complete on Android without App Links.
 */
const GoogleMobileCallback = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const hasOAuthResult =
    searchParams.has("code") || searchParams.has("error");
  const isMobile = useMemo(() => isLikelyMobileDevice(), []);
  const showMobileCopy = hasOAuthResult || isMobile;
  const deepLink = useMemo(
    () => buildAppOAuthDeepLink(window.location.search || ""),
    // Recompute when OAuth params change (code land).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchParams.toString()]
  );

  useEffect(() => {
    if (!hasOAuthResult) {
      return;
    }

    const jump = () => {
      try {
        window.location.replace(deepLink);
      } catch {
        window.location.href = deepLink;
      }
    };

    jump();
    const timer = window.setTimeout(jump, 400);

    return () => window.clearTimeout(timer);
  }, [deepLink, hasOAuthResult]);

  return (
    <ParticlesAuth>
      <div className="auth-page-content mt-lg-5">
        <Container>
          <Row>
            <Col lg={12}>
              <div className="text-center mt-sm-5 mb-4 text-white-50">
                <AdatisLogo height={48} />
              </div>
            </Col>
          </Row>
          <Row className="justify-content-center">
            <Col md={8} lg={6} xl={5}>
              <Card className="mt-4">
                <CardBody className="p-4 text-center">
                  <h5 className="text-primary mb-3">
                    {t(
                      showMobileCopy
                        ? "auth.google.mobileCallback.title"
                        : "auth.google.mobileCallback.desktopTitle"
                    )}
                  </h5>
                  <p className="text-muted mb-4">
                    {t(
                      showMobileCopy
                        ? "auth.google.mobileCallback.body"
                        : "auth.google.mobileCallback.desktopBody"
                    )}
                  </p>
                  {showMobileCopy ? (
                    <Spinner color="primary" className="mb-3" />
                  ) : null}
                  {hasOAuthResult ? (
                    <div className="mb-3">
                      <a className="btn btn-primary w-100" href={deepLink}>
                        {t("auth.google.mobileCallback.openApp")}
                      </a>
                    </div>
                  ) : null}
                  <div>
                    <Link to="/login" className="fw-medium text-primary">
                      {t("auth.google.callback.backToLogin")}
                    </Link>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </ParticlesAuth>
  );
};

export default GoogleMobileCallback;
